'use client';
'use memo';

import type { RefObject } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';

import { useMounted } from '@zoomit/primitives';

type Props<T extends Element = HTMLElement> = {
  ref: RefObject<T | null>;
  className: string;
};

export function MovingBorder<T extends Element = HTMLElement>({ ref, className }: Props<T>) {
  const [{ height, width }, setDimensions] = useState({ width: 0, height: 0 });

  const strokeWidth = 2;
  const halfStroke = strokeWidth / 2;
  const radius = height / 2 - halfStroke;

  const path = `M ${radius + halfStroke} ${halfStroke} H ${
    width - radius - halfStroke
  } A ${radius} ${radius} 0 0 1 ${width - halfStroke} ${
    radius + halfStroke
  } V ${height - radius - halfStroke} A ${radius} ${radius} 0 0 1 ${
    width - radius - halfStroke
  } ${height - halfStroke} H ${radius + halfStroke} A ${radius} ${radius} 0 0 1 ${halfStroke} ${
    height - radius - halfStroke
  } V ${radius + halfStroke} A ${radius} ${radius} 0 0 1 ${radius + halfStroke} ${halfStroke} Z`;

  useEffect(() => {
    if (ref.current) {
      const { width, height } = ref.current.getBoundingClientRect();
      setDimensions({ width, height });
    }
  }, []);

  if (!width || !height) {
    return null;
  }

  return (
    <div className="absolute inset-0 rounded-full blur-[7px]">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none">
        <circle r={height / 2} className={className}>
          <animateMotion dur="4s" repeatCount="indefinite" path={path} />
        </circle>
      </svg>
    </div>
  );
}
